import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { KeuringStatusControl } from '../components/KeuringStatusControl';
import { ClipboardCheck, Zap, Wrench, ExternalLink, Loader2 } from 'lucide-react';

const OPEN_STATUSES = ['aangevraagd', 'ingepland'];

export default function Keuringen() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');

  const load = useCallback(() => {
    api.get('/keuringen')
      .then(res => setItems(res.data))
      .catch(() => toast.error('Kon keuringen niet laden'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = filter === 'open'
    ? items.filter(i => OPEN_STATUSES.includes(i.keuring_request?.status))
    : items;
  const openCount = items.filter(i => OPEN_STATUSES.includes(i.keuring_request?.status)).length;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="keuringen-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-lg font-semibold">Keuringen</h1>
            <p className="text-xs text-muted-foreground">{openCount} openstaande keuringsaanvra{openCount === 1 ? 'ag' : 'gen'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant={filter === 'open' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('open')} data-testid="filter-open">
            Openstaand ({openCount})
          </Button>
          <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')} data-testid="filter-all">
            Alle ({items.length})
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : visible.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          {filter === 'open' ? 'Geen openstaande keuringsaanvragen 🎉' : 'Nog geen keuringsaanvragen geregistreerd'}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {visible.map(item => (
            <Card key={`${item.source}-${item.id}`} data-testid={`keuring-item-${item.id}`}>
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className={item.source === 'schema' ? 'bg-purple-50 text-purple-700 border-purple-300' : 'bg-blue-50 text-blue-700 border-blue-300'}>
                  {item.source === 'schema' ? <Zap className="w-3 h-3 mr-1" /> : <Wrench className="w-3 h-3 mr-1" />}
                  {item.source === 'schema' ? 'Schema' : 'Installatie'}
                </Badge>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-medium">{item.customer_name || item.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.address || '—'}
                    {item.keuring_request?.requested_at && ` · aangevraagd op ${new Date(item.keuring_request.requested_at).toLocaleDateString('nl-BE')}`}
                    {item.keuring_request?.notes && ` · ${item.keuring_request.notes}`}
                  </p>
                </div>
                <KeuringStatusControl
                  installation={{ id: item.id, keuring_request: item.keuring_request }}
                  endpoint={item.source === 'schema' ? `/schemas/${item.id}/keuring` : `/scheduled-installations/${item.id}/keuring`}
                  onUpdated={(kr) => setItems(prev => prev.map(x => x.id === item.id && x.source === item.source ? { ...x, keuring_request: kr } : x))}
                />
                {item.source === 'schema' && (
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate(`/schemas/${item.id}`)} data-testid={`open-schema-${item.id}`}>
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Openen
                  </Button>
                )}
                {item.source === 'installatie' && (
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate('/installaties')} data-testid={`open-installatie-${item.id}`}>
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Openen
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
